import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar,
  Clock,
  MapPin,
  User,
  CheckCircle,
  XCircle,
  TrendingUp, 
  AlertCircle,
  Briefcase,
  ChevronDown,
  ChevronUp,
  Filter,
  Users
} from 'lucide-react';

const AttendanceReport = ({ data }) => {
  const [expandedRecord, setExpandedRecord] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState({
    start_date: '2026-03-01',
    end_date: '2026-03-01',
  });

  console.log("Attendance data received:", data);

  // Process and group attendance data correctly
  const processedData = useMemo(() => {
    if (!data?.data?.attendances) {
      return null;
    }

    const attendances = data.data.attendances;
    
    // Group by date and user
    const groupedByDateAndUser = {};
    
    attendances.forEach(attendance => {
      const date = attendance.attendance_date.split('T')[0];
      const userId = attendance.user_id;
      const key = `${date}_${userId}`;
      
      if (!groupedByDateAndUser[key]) {
        groupedByDateAndUser[key] = {
          date: date,
          user: attendance.user,
          user_id: userId,
          records: [],
          total_working_minutes: 0,
          total_late_minutes: 0,
          total_overtime_minutes: 0,
          status: 'present', // Will be updated based on records
          shifts: []
        };
      }
      
      groupedByDateAndUser[key].records.push(attendance);
      groupedByDateAndUser[key].total_working_minutes += attendance.net_working_minutes;
      groupedByDateAndUser[key].total_late_minutes += attendance.late_minutes;
      groupedByDateAndUser[key].total_overtime_minutes += attendance.overtime_minutes;
      groupedByDateAndUser[key].shifts.push(attendance.shift.name);
      
      // Determine overall status for the day
      if (attendance.status === 'late') {
        groupedByDateAndUser[key].status = 'late';
      } else if (attendance.status === 'absent' && groupedByDateAndUser[key].status !== 'late') {
        groupedByDateAndUser[key].status = 'absent';
      }
    });
    
    // Convert to array and sort by date and user
    const groupedAttendances = Object.values(groupedByDateAndUser)
      .sort((a, b) => a.date.localeCompare(b.date));
    
    // Calculate correct summary statistics
    const uniqueDates = new Set(groupedAttendances.map(item => item.date));
    const totalDays = uniqueDates.size;
    const totalUserDays = groupedAttendances.length;
    
    let presentDays = 0;
    let lateDays = 0;
    let absentDays = 0;
    
    groupedAttendances.forEach(item => {
      if (item.status === 'present') presentDays++;
      else if (item.status === 'late') lateDays++;
      else if (item.status === 'absent') absentDays++;
    });
    
    const totalMinutes = groupedAttendances.reduce((sum, item) => sum + item.total_working_minutes, 0);
    const totalHours = totalMinutes / 60;
    const averageHours = totalUserDays > 0 ? totalHours / totalUserDays : 0;
    
    return {
      summary: {
        total_calendar_days: totalDays,
        total_user_days: totalUserDays,
        present_days: presentDays,
        late_days: lateDays,
        absent_days: absentDays,
        total_working_hours: totalHours.toFixed(2),
        average_working_hours: averageHours.toFixed(2),
        total_employees: new Set(groupedAttendances.map(item => item.user_id)).size
      },
      attendances: groupedAttendances
    };
  }, [data]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'present': return 'text-green-600 bg-green-100';
      case 'late': return 'text-yellow-600 bg-yellow-100';
      case 'absent': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getApprovalStatusColor = (status) => {
    switch(status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    return time.substring(0, 5);
  };

  const formatMinutesToHours = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  // Show loading state
  if (!processedData) {
    return (
      <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading attendance data...</p>
        </div>
      </div>
    );
  }

  const filteredAttendances = processedData.attendances.filter(attendance => {
    if (filterStatus === 'all') return true;
    return attendance.status === filterStatus;
  });

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-800">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  const SummaryCard = ({ title, value, subtitle, icon: Icon }) => (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <p className="text-xl font-bold text-gray-800">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        {Icon && <Icon className="w-8 h-8 text-blue-400" />}
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Attendance Report</h1>
        {/* <p className="text-gray-600">
          {new Date(dateRange.start_date).toLocaleDateString()} - {new Date(dateRange.end_date).toLocaleDateString()}
        </p> */}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Calendar Days"
          value={processedData.summary.total_calendar_days}
          icon={Calendar}
          color="bg-blue-500"
          subtitle="Unique dates in range"
        />
        <StatCard
          title="Employee Days"
          value={processedData.summary.total_user_days}
          icon={Users}
          color="bg-purple-500"
          subtitle="Total attendance records"
        />
        <StatCard
          title="Present"
          value={processedData.summary.present_days}
          icon={CheckCircle}
          color="bg-green-500"
        />
        <StatCard
          title="Late"
          value={processedData.summary.late_days}
          icon={AlertCircle}
          color="bg-yellow-500"
        />
      </div>

      {/* Working Hours Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <SummaryCard
          title="Total Working Hours"
          value={`${processedData.summary.total_working_hours} hrs`}
          subtitle={`Across all employees`}
          icon={Clock}
        />
        <SummaryCard
          title="Average Working Hours"
          value={`${processedData.summary.average_working_hours} hrs/day`}
          subtitle="per employee per day"
          icon={TrendingUp}
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filter by status:</span>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All ({processedData.attendances.length})</option>
              <option value="present">Present ({processedData.summary.present_days})</option>
              <option value="late">Late ({processedData.summary.late_days})</option>
              <option value="absent">Absent ({processedData.summary.absent_days})</option>
            </select>
          </div>
          <div className="text-sm text-gray-500">
            Showing {filteredAttendances.length} of {processedData.attendances.length} records
          </div>
        </div>
      </div>

      {/* Attendance Records - Grouped by Date and User */}
      <div className="space-y-4">
        {filteredAttendances.map((group) => (
          <div key={`${group.date}_${group.user_id}`} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
            {/* Record Header */}
            <div 
              className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setExpandedRecord(expandedRecord === `${group.date}_${group.user_id}` ? null : `${group.date}_${group.user_id}`)}
            >
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2 flex-wrap gap-2">
                    <User className="w-5 h-5 text-gray-500" />
                    <h3 className="text-lg font-semibold text-gray-800">
                      {group.user?.first_name} {group.user?.last_name}
                    </h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(group.status)}`}>
                      {group.status?.toUpperCase()}
                    </span>
                    {group.records.length > 1 && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-600">
                        {group.records.length} Shifts
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">
                        {new Date(group.date).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">
                        Total: {formatMinutesToHours(group.total_working_minutes)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Briefcase className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">
                        {group.shifts.join(', ')}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  {group.total_late_minutes > 0 && (
                    <div className="text-right">
                      <p className="text-sm text-yellow-600">Late Total</p>
                      <p className="font-semibold text-yellow-600">
                        {group.total_late_minutes} min
                      </p>
                    </div>
                  )}
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Working Hours</p>
                    <p className="font-semibold text-gray-800">
                      {formatMinutesToHours(group.total_working_minutes)}
                    </p>
                  </div>
                  {expandedRecord === `${group.date}_${group.user_id}` ? 
                    <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  }
                </div>
              </div>
            </div>

            {/* Expanded Details - Show all shifts for the day */}
            {expandedRecord === `${group.date}_${group.user_id}` && (
              <div className="border-t border-gray-200 bg-gray-50">
                {group.records.map((attendance, index) => (
                  <div key={attendance.id} className={`p-6 ${index !== group.records.length - 1 ? 'border-b border-gray-200' : ''}`}>
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        Shift {index + 1}: {attendance.shift?.name}
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getApprovalStatusColor(attendance.approval_status)}`}>
                          {attendance.approval_status?.toUpperCase()}
                        </span>
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {/* Timing Details */}
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-700">Timing</p>
                        <p className="text-sm">
                          <span className="text-gray-500">Clock In:</span>{' '}
                          {formatTime(attendance.clock_in)}
                        </p>
                        <p className="text-sm">
                          <span className="text-gray-500">Clock Out:</span>{' '}
                          {formatTime(attendance.clock_out)}
                        </p>
                        <p className="text-sm">
                          <span className="text-gray-500">Expected:</span>{' '}
                          {formatTime(attendance.expected_clock_in)} - {formatTime(attendance.expected_clock_out)}
                        </p>
                        {attendance.late_minutes > 0 && (
                          <p className="text-sm text-yellow-600">
                            ⏰ Late by: {attendance.late_minutes} minutes
                          </p>
                        )}
                        {attendance.overtime_minutes > 0 && (
                          <p className="text-sm text-green-600">
                            ✨ Overtime: {formatMinutesToHours(attendance.overtime_minutes)}
                          </p>
                        )}
                      </div>

                      {/* Location Details */}
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-700">Location</p>
                        <div className="flex items-start space-x-2">
                          <MapPin className="w-4 h-4 text-gray-400 mt-1" />
                          <div>
                            <p className="text-sm">{attendance.clock_in_location_name || 'N/A'}</p>
                            <p className="text-xs text-gray-500 font-mono">
                              {attendance.clock_in_latitude}, {attendance.clock_in_longitude}
                            </p>
                            <p className="text-xs text-gray-500">
                              Accuracy: {attendance.geo_accuracy}m
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Working Details */}
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-700">Working Hours</p>
                        <p className="text-sm">
                          <span className="text-gray-500">Total Working:</span>{' '}
                          {formatMinutesToHours(attendance.total_working_minutes)}
                        </p>
                        <p className="text-sm">
                          <span className="text-gray-500">Break:</span>{' '}
                          {formatMinutesToHours(attendance.break_minutes)}
                        </p>
                        <p className="text-sm">
                          <span className="text-gray-500">Net Working:</span>{' '}
                          <span className="font-semibold">
                            {formatMinutesToHours(attendance.net_working_minutes)}
                          </span>
                        </p>
                        <p className="text-sm">
                          <span className="text-gray-500">Verification:</span>{' '}
                          {attendance.verification_method}
                        </p>
                        <p className="text-xs text-gray-500">
                          IP: {attendance.ip_address}
                        </p>
                      </div>
                    </div>

                    {/* Remarks */}
                    {attendance.remarks && (
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <p className="text-sm">
                          <span className="text-gray-500 font-medium">Remarks:</span>{' '}
                          {attendance.remarks}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredAttendances.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No attendance records found</h3>
          <p className="text-gray-500">Try adjusting your filters or date range</p>
        </div>
      )}
    </div>
  );
};

export default AttendanceReport;